import FormLayout from "../../../components/FormLayout";
import CustomButton from "../../../components/CustomButton";
import { CitySorryContact } from "./CitySorryContact";
import style from "./Sorry.module.scss";
import { Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/index";
import { ExitType } from "../../../constants/common"
import SorryFallback from "../../../components/SorryFallback";
import { useEffect } from "react";
import ErrorAndBackButton from "../../../components/ErrorAndBackButton";
import { useCurrentTaxYear } from "../../../hooks/TaxYear";

/*
 * Returns a JSX element based on primarily on ExitType,
 * some additional conditional stuff based on urlPath,
 * and error metadata on the History state object.
 */
function ExitCodeContent({exitCode, urlPath, historyState}: {exitCode: ExitType, urlPath: string, historyState: any}) {
    const userError = useSelector((store: RootState) => store.user.error);
  switch (exitCode) {
      case ExitType.NoTermsAcceptance:
        let noTermsDescription = "All users must opt in to our terms of service to use LIME.";
        return (
          <>
            <CitySorryContact description={noTermsDescription} />
            <Link to="/">
            <CustomButton
              variant="contained"
              label="Back to Lime Login"
             />
            </Link>
          </>
        );
      case ExitType.OutsideCounty:
        return (
          <>
            {
              `Based on the address you’ve entered, you are not living within the
                  Springboro City limits and do not need to file a return with the City. If you feel
                  you have gotten this message in error, please contact our Tax Department at 877-957-LIME
                  for additional assistance.`
              }
            { urlPath != "/user/sorry" && 
              <Link to="/">
                <CustomButton
                  variant="contained"
                  label={"Back to Lime Login"}
                 />
              </Link>
            }
          </>
        );
      case ExitType.FiledSchEAnd4797:
      case ExitType.FiledSchE:
      case ExitType.Filed4797:
        let filingContext = "You indicated that you filed Schedule E and/or Form 4797 with your Federal tax return. Unfortunately, LIME cannot be used to file a Springboro return reporting this type of business income.";
        return (
          <CitySorryContact description={filingContext} />
        );
      case ExitType.PartTimeResident:
        // Represents a user who has re-verified their address, but that address is not within the city
        return (
          <>
            {`We’re sorry, but this address does not appear to be located in the City of Springboro. In order to use LIME, you must have lived in Springboro for the entire tax year.`}
            <ul>
              <li>
                If you’d like help preparing your part-year return, <a href={`mailto:${import.meta.env.VITE_APP_SAAS_CUSTOMER_EMAIL}`} className={style.sendLink} >send us a message</a>
                &#160;or call us at <label className={style.labelBold}>{import.meta.env.VITE_APP_SAAS_CUSTOMER_PHONE}</label>.
              </li>
              <li>If you believe you have gotten this message in error, please call us at <label className={style.labelBold}>{import.meta.env.VITE_APP_SAAS_CUSTOMER_PHONE}</label>.</li>
            </ul>
          </>
        );
      case ExitType.UploadedSch1WithRoyaltiesAndOtherGains:
      case ExitType.UploadedSch1WithRoyalties:
      case ExitType.UploadedSch1WithOtherGains:
        let uploadContext = "Your Schedule 1 indicates that you filed Schedule E and/or Form 4797 with your Federal tax return. We’re sorry, but LIME cannot be used to file a Springboro return reporting this type of business income.";
        return (
            <CitySorryContact description={uploadContext} />
          );
      case ExitType.UserData:
        return <>
          { "Failed to save your data." }
          <ErrorAndBackButton errorMessage={String(userError)} />
          </>
      case ExitType.UnknownAddressVerificationError:
        return <>
          <ErrorAndBackButton
            errorMessage="We encountered an error during address verification. Please double-check your address and try again."
          />
        </>
      case ExitType.UserIsAMinor:
        const currentTaxYear = useCurrentTaxYear();
        // TODO SAAS: the number below is different than the one set in an env var..
        return <>
          {`If you turned 18 in ${currentTaxYear}, please call the Tax Department at 937-748-9701 for assistance in filing your return.` }
          <br/>
          <br/>
          {`If you were under the age of 18 for all of ${currentTaxYear}, you are not required to file a tax return with the City.`}
        </>
      default:
        /*
         * I can't remember how I figured this out,
         * but if we can't handle an error above then
         * we need to investigate the history state
         * and show or suppress the backend error message
         * depending on environment in the SorryFallback component
         */
        return (<SorryFallback historyState={historyState} />);
    }
};


/*
 * This component handles recoverable
 * business logic errors for the whole application.
 * It's driven largely by the exitMessageType property in the userSetup Redux store.
 * TODO: that property should be refactored into a different store
 *
 * If an errors can't be handled gracefully this falls back to a generic error page
 * based on the content in the History state and will get logged to Sentry
 * (See: https://developer.mozilla.org/en-US/docs/Web/API/History/state)
 */
const Sorry = () => {
  const { state, pathname } = useLocation();
  const { exitMessageType } = useSelector((store: RootState) => store.userSetup);
  useEffect(() => { 
    document.title = import.meta.env.VITE_APP_NAME + ' - Sorry';
  }, [location]);

  return (
    <FormLayout title={`Sorry`} subtitle={""} >
      <div>
        <ExitCodeContent exitCode={exitMessageType} urlPath={pathname} historyState={state}/>
      </div>
    </FormLayout>
  );
}

export default Sorry;
